import { Controller, UseFormReturn } from 'react-hook-form';
import { makeStyles } from '@material-ui/styles';

import { Text } from '@bizzabo/design-system';

import TimezonePicker from '../../../meetings/TimezonePicker/TimezonePicker';
import {
    BUFFER_TIME_OPTIONS,
    DURATION_OPTIONS,
    LEAD_TIME_OPTIONS,
    MAX_MEETINGS_OPTIONS,
} from './useSchedulingPreferencesForm/formConstants';
import { SchedulingPreferencesFormValues } from './useSchedulingPreferencesForm/types';
import AvailabilityDaysPicker from './AvailabilityDaysPicker';
import SchedulingPreferencesSelect from './SchedulingPreferencesSelect';
import useTypeTracking from '../../../analytics';
import SelectController from './SelectController';
import { formatAvailability, parseAvailability } from './useSchedulingPreferencesForm';
import { groupTimeIntervalsByZonedDay } from './dateUtils';
import { SchedulingHour } from '../../hooks/useSchedulingPreferences/types';
import { prepareAvailabilityByTotalTime } from './helpers';

export interface SchedulingPreferencesFormFieldsProps {
    form: UseFormReturn<SchedulingPreferencesFormValues, object>;
    schedulingHoursBase: SchedulingHour[];
}

const SchedulingPreferencesFormFields: React.FC<SchedulingPreferencesFormFieldsProps> = ({
    form,
    schedulingHoursBase,
}) => {
    const { control, register, getValues, setValue, formState, setError, clearErrors } = form;

    const { trackClickEvent } = useTypeTracking({
        data: { category: 'MEETINGS_SETTINGS' },
    });

    const classes = useStyles();

    const handleTimeZoneChangeForAvailableTimes = (
        previousTimeZone: string,
        currentTimeZone: string
    ) => {
        const values = getValues();
        const availabilityInUTC = formatAvailability(values.availability, previousTimeZone);
        const availabilityInCurrentTimeZone = parseAvailability(
            groupTimeIntervalsByZonedDay(availabilityInUTC, currentTimeZone),
            currentTimeZone
        );
        const availability = prepareAvailabilityByTotalTime(
            availabilityInCurrentTimeZone,
            schedulingHoursBase,
            currentTimeZone
        );
        setValue('availability', availability);
    };

    return (
        <div>
            <div>
                <Text variant="t30" className={classes.header}>
                    Schedule Preferences
                </Text>
            </div>
            <div>
                <Text variant="t71">Select Time Zone</Text>
            </div>
            <div>
                <Text variant="t81" className={classes.helperText}>
                    This applies to your schedule view only.
                </Text>
            </div>
            <div className={classes.section}>
                <Controller
                    name="timezoneId"
                    control={control}
                    render={({ field: { onChange, ref: _ref, ...selectProps } }) => (
                        <TimezonePicker
                            {...selectProps}
                            onChange={({ value }) => {
                                handleTimeZoneChangeForAvailableTimes(
                                    getValues().timezoneId,
                                    value
                                );
                                onChange(value);
                                trackClickEvent({ subCategory: 'EDIT_TIMEZONE' });
                            }}
                        />
                    )}
                />
            </div>
            <div>
                <Text variant="t71">Select Meeting Availability</Text>
            </div>
            <div>
                <Text variant="t81" className={classes.helperText}>
                    Select the times you would like to appear as available to other attendees.
                </Text>
            </div>
            <div className={classes.section}>
                <AvailabilityDaysPicker
                    {...{
                        control,
                        register,
                        getValues,
                        setValue,
                        formState,
                        setError,
                        clearErrors,
                    }}
                />
            </div>
            <div className={classes.section}>
                <Text variant="t71">Meeting Duration</Text>
                <div>
                    <Text variant="t81" className={classes.helperText}>
                        Select your desired default meeting length.
                    </Text>
                </div>
                <SelectController
                    name="meetingDuration"
                    options={DURATION_OPTIONS}
                    control={control}
                    render={({ field: { onChange, ref: _ref, ...selectProps } }) => (
                        <SchedulingPreferencesSelect
                            {...selectProps}
                            className={classes.formSelect}
                            onChange={value => {
                                onChange(value);
                                trackClickEvent({ subCategory: 'EDIT_MEETING_DURATION' });
                            }}
                        />
                    )}
                />
            </div>
            <div>
                <Text variant="t71">Buffer Time</Text>
            </div>
            <div>
                <Text variant="t81" className={classes.helperText}>
                    Add buffer time before and after meetings to give yourself a break or prepare
                    for the next meeting.
                </Text>
            </div>
            <div className={classes.section}>
                <SelectController
                    name="bufferTime"
                    options={BUFFER_TIME_OPTIONS}
                    control={control}
                    render={({ field: { onChange, ref: _ref, ...selectProps } }) => (
                        <SchedulingPreferencesSelect
                            {...selectProps}
                            className={classes.formSelect}
                            onChange={value => {
                                onChange(value);
                                trackClickEvent({ subCategory: 'EDIT_BUFFER_TIME' });
                            }}
                        />
                    )}
                />
            </div>
            <div>
                <Text variant="t71">Lead Time</Text>
            </div>
            <div>
                <Text variant="t81" className={classes.helperText}>
                    Avoid surprise bookings by establishing a lead time rule.
                </Text>
            </div>
            <div className={classes.section}>
                <SelectController
                    name="leadTime"
                    options={LEAD_TIME_OPTIONS}
                    control={control}
                    render={({ field: { onChange, ref: _ref, ...selectProps } }) => (
                        <SchedulingPreferencesSelect
                            {...selectProps}
                            className={classes.formSelect}
                            onChange={value => {
                                onChange(value);
                                trackClickEvent({ subCategory: 'EDIT_LEAD_TIME' });
                            }}
                        />
                    )}
                />
            </div>
            <div>
                <Text variant="t71">Max Meetings Allowed Per Day</Text>
            </div>
            <div>
                <Text variant="t81" className={classes.helperText}>
                    Limit the number of meetings to a certain number per day.
                </Text>
            </div>
            <div className={classes.section}>
                <SelectController
                    name="maxMeetingsPerDay"
                    options={MAX_MEETINGS_OPTIONS}
                    control={control}
                    render={({ field: { onChange, ref: _ref, ...selectProps } }) => (
                        <SchedulingPreferencesSelect
                            {...selectProps}
                            className={classes.formSelect}
                            onChange={value => {
                                onChange(value);
                                trackClickEvent({ subCategory: 'EDIT_MEETINGS_PER_DAY' });
                            }}
                        />
                    )}
                />
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        header: {
            marginBottom: 16,
        },
        helperText: {
            color: theme.customization.interactive.G40,
            marginBottom: 16,
        },
        formSelect: {
            height: 32,
            width: 200,
        },
        section: {
            marginBottom: 24,
        },
    }),
    { classNamePrefix: 'people-settings-scheduling-preference-form' }
);

export default SchedulingPreferencesFormFields;
